﻿import { IInputScope, KbInput } from "@app/components/kb-input/kb-input.component";
import { NgComponent } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { Dirs } from "@app/helpers/dirs";
import { ImageSelectHelper } from "@app/helpers/image-select.helper";
import { eOptionLayout } from "@models";
import { eScrollMode, Utils } from "@tools";

export interface IImageSelectScope extends IInputScope {
    // external members
    source: any[];
    data: any; // custom data that can be passed in and accessed by the transcluded content for the select items
    valueField: string;
    labelField: string;
    itemStyle: any;
    optionLayout: string;

    // internal directive members
    _selectedItem: any;
    _select: (item: any) => void;
    _helpClick: (e: ng.IAngularEvent) => void;

    _paged: boolean;
    _scrollNext: () => void;
    _scrollPrev: () => void;
    _pageNextVisible: boolean;
    _pagePrevVisible: boolean;
}

export interface IImageSelectController {
    scope: IImageSelectScope;
}

@NgComponent({
    token: Token.KbImageSelect,
    dependencies: [
        Token.$Timeout,
        Token.$Window
    ]
})
export class KbImageSelect extends KbInput {
    constructor($timeout: ng.ITimeoutService, $window: ng.IWindowService) {
        super($timeout);

        this.template = Dirs.component("kb-image-select");
        this.scope = Utils.extend(this.scope, {
            source: "=",
            data: "=?",
            valueField: "@",
            labelField: "@",
            itemStyle: "=?",
            optionLayout: "=?"
        });
        this.transclude = true;

        this.link = (scope: IImageSelectScope, element: JQuery, atts, controllers: any[]) => {
            this.kbInputLinkFn(scope, element, atts, controllers, false);
            let helper = new ImageSelectHelper($window, $timeout, scope, element);

            let ngModel = controllers[0] as ng.INgModelController;

            let getItemValue = (item: any) => {
                if (item === null || item === undefined) return null;
                return scope.valueField ? item[scope.valueField] : item;
            };
            let getItemLabel = (item: any): string => {
                if (scope.labelField && item[scope.labelField]) {
                    return item[scope.labelField];
                } else if (scope.valueField) {
                    return item[scope.valueField];
                } else {
                    return item;
                }
            };

            let isEnabled = () => {
                return !scope.ngDisabled;
            };

            let updateSelected = () => {
                let selected = null;
                if (scope.source) {
                    for (let item of scope.source) {
                        item.$selected = false;
                        if (getItemValue(item) == ngModel.$viewValue) {
                            selected = item;
                            item.$selected = true;
                        }
                    }
                }
                scope._selectedItem = selected;
            };
                
            // options might be added after the ngModel is set by the consumer, so
            // we need to refind the selected option 
            scope.$watchCollection("source", () => {
                updateSelected();
            });

            ngModel.$render = () => {
                updateSelected();
            };

            scope._select = (item: any) => {
                if (isEnabled()) {
                    ngModel.$setViewValue(getItemValue(item));
                    scope._selectedItem = item;
                    for (let item of scope.source) item.$selected = false;
                    item.$selected = true;
                }
            };

            scope._helpClick = e => {
                e.stopPropagation();
            };

            let moveUp = () => {
                if (scope.source && scope._selectedItem) {
                    let curIndex = scope.source.indexOf(scope._selectedItem);
                    let newIndex = curIndex - 1;
                    if (newIndex >= 0) scope._select(scope.source[newIndex]);
                } else if (scope.source && scope.source.length) {
                    scope._select(scope.source[0]);
                }
            };

            let moveDown = () => {
                if (scope.source && scope._selectedItem) {
                    let curIndex = scope.source.indexOf(scope._selectedItem);
                    let newIndex = curIndex + 1;
                    if (newIndex < scope.source.length) scope._select(scope.source[newIndex]);
                } else if (scope.source && scope.source.length) {
                    scope._select(scope.source[0]);
                }
            };
            
            let scrollSelectedIntoView = () => {
                if (scope.optionLayout == eOptionLayout.scroll) {
                    $timeout(() => {
                        let $selected = element.find(".kb-option.selected");
                        let selectedElem = $selected.length ? $selected[0] : null;
                        if (selectedElem) {
                            Utils.scrollIntoView({
                                elem: selectedElem,
                                mode: eScrollMode.nearest,
                                horizontal: true,
                                animate: true,
                                nearestEndPadding: parseInt(selectedElem.style.marginRight) + selectedElem.offsetWidth/2
                            });
                        }
                    });
                }
            }

            scope.$watch("_selectedItem", () => {
                scrollSelectedIntoView();
            });

            
            // bind keyboard events: arrows up(38) / down(40), enter(13) and tab(9), esc(27)
            element.on("keydown", (e: JQueryEventObject) => {
                if (e.key == 'ArrowLeft' || e.key == 'ArrowUp') {
                    e.preventDefault();
                    scope.$apply(() => {
                        moveUp();
                    });
                }
                if (e.key == 'ArrowRight' || e.key == 'ArrowDown') {
                    e.preventDefault();
                    scope.$apply(() => {
                        moveDown();
                    });
                }
                if (e.key == 'Escape') {
                    element.trigger('blur');
                }
            });

            scope.$on("$destroy", () => {
                element.off();
                // $($window).off("resize", windowResize);
            });

        };
    }
}
